import type {
  FormDefinition,
  FormFieldDefinition,
  Shell,
  ShellFeld,
  ShellFreitextAntwort,
  ShellQuellen,
  ShellWenn,
  ShellWert,
  ShellWerte,
} from '@/core/templates/types'
import { getAt, setAt } from '@/core/lib/path'

export const DATE_SHAPE = /^[\dY]{4}-[\dM]{2}-[\dD]{2}$/
const ISO_RE = /^\d{4}-\d{2}-\d{2}$/

export type FormValues = Record<string, Record<string, string | boolean>>

function initialField(field: FormFieldDefinition, saved: unknown): string | boolean {
  if (field.control === 'checkbox') return typeof saved === 'boolean' ? saved : field.initial === true
  if (field.control === 'select' || field.control === 'display' || field.dictation) return typeof saved === 'string' ? saved : ''
  const fallback = typeof field.initial === 'string' ? field.initial : ''
  const value = typeof saved === 'string' ? saved : fallback
  if (field.control === 'date' || (field.control === 'text-or-date' && DATE_SHAPE.test(value))) return ISO_RE.test(value) ? value : ''
  return value
}

export function initialFormValues(definition: FormDefinition, saved?: Record<string, unknown> | null): FormValues {
  const values: FormValues = {}
  for (const { fields } of Object.values(definition.sections)) {
    for (const field of fields) setAt(values, field.id, initialField(field, getAt(saved, field.id)))
  }
  return values
}

export function requiredValueEmpty(value: unknown): boolean {
  if (typeof value === 'string') return isBlankText(value)
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) return isBlankText((value as { text?: unknown }).text)
  return value == null
}

export function updateFormField<T extends FormValues | null>(values: T, path: string, value: unknown): T {
  if (!values) return values
  const next = structuredClone(values)
  setAt(next, path, value)
  return next
}

function shellAnswerLabels(value: ShellWert | undefined): Set<string> {
  if (value == null) return new Set()
  if (typeof value === 'string') return new Set([value])
  if (typeof value === 'number') return new Set()
  if (Array.isArray(value)) return new Set(value.map((v) => (typeof v === 'string' ? v : v.label)))
  return new Set([value.label])
}

export function shellWennApplies(wenn: ShellWenn | undefined, values: ShellWerte, quellen: ShellQuellen = {}): boolean {
  if (!wenn) return true
  const current = values[wenn.feld]
  if ('groesser_als' in wenn) {
    return typeof current === 'number' && current > wenn.groesser_als
  }
  const labels = shellAnswerLabels(current)
  if (wenn.ist.some((label) => labels.has(label))) return true
  return wenn.oder_quelle !== undefined && labels.size === 0 && quellen[wenn.feld] === wenn.oder_quelle
}

export function shellFelder(shell: Shell): ShellFeld[] {
  return shell.abschnitte.flatMap((a) => a.felder ?? [])
}

export function vortextLabel(feld: ShellFeld): string {
  const first = feld.optionen?.[0] ?? feld.gruppen?.[0]?.optionen[0]
  const optionLabel = typeof first === 'string' || first === undefined ? first : first.label
  return `Text zu „${feld.label || optionLabel || ''}“`
}

function vortextFeld(feld: ShellFeld): ShellFeld[] {
  if (!feld.vortext) return []
  const leaf: ShellFeld = { id: feld.vortext.id, label: vortextLabel(feld), art: 'mehrzeilig' }
  return [feld.nur_lesen ? { ...leaf, nur_lesen: true } : leaf]
}

export function collectVisibleShellLeafFelder(
  felder: readonly ShellFeld[],
  values: ShellWerte,
  quellen: ShellQuellen = {},
  out: ShellFeld[] = [],
): ShellFeld[] {
  for (const feld of felder) {
    if (!shellWennApplies(feld.wenn, values, quellen)) continue
    if (feld.art === 'gruppe') {
      if (feld.felder) collectVisibleShellLeafFelder(feld.felder, values, quellen, out)
      continue
    }
    if (feld.art === 'zwischentitel') continue
    out.push(feld, ...vortextFeld(feld))
  }
  return out
}

export function collectAllShellLeafIds(felder: readonly ShellFeld[], out: Set<string> = new Set()): Set<string> {
  for (const feld of felder) {
    if (feld.art === 'gruppe') {
      if (feld.felder) collectAllShellLeafIds(feld.felder, out)
      continue
    }
    if (feld.art === 'zwischentitel') continue
    out.add(feld.id)
    if (feld.vortext) out.add(feld.vortext.id)
  }
  return out
}

export function dropHiddenShellWerte(shell: Shell, werte: ShellWerte, quellen: ShellQuellen = {}): ShellWerte {
  const felder = shellFelder(shell)
  const allIds = collectAllShellLeafIds(felder)
  let current = werte
  for (;;) {
    const visible = new Set(collectVisibleShellLeafFelder(felder, current, quellen).map((f) => f.id))
    const hidden = new Set([...allIds].filter((id) => !visible.has(id) && id in current))
    if (hidden.size === 0) return current
    current = Object.fromEntries(Object.entries(current).filter(([id]) => !hidden.has(id)))
  }
}

export function isBlankText(value: unknown): boolean {
  return typeof value !== 'string' || value.trim() === ''
}

function isBlankFreitextAnswer(entry: string | ShellFreitextAntwort): boolean {
  return typeof entry !== 'string' && isBlankText(entry.text)
}

export const WHOLE_NUMBER = /^-?\d+$/

export const ZAHL_MAX = 2_147_483_647

export function isZahlOutOfRange(n: number): boolean {
  return !Number.isInteger(n) || n < 0 || n > ZAHL_MAX
}

export function isZahlValuePresentAndInvalid(value: ShellWert | undefined): boolean {
  if (value == null || value === '') return false
  if (typeof value === 'number') return isZahlOutOfRange(value)
  if (typeof value === 'string') return !WHOLE_NUMBER.test(value) || isZahlOutOfRange(Number(value))
  return false
}

export function isShellFeldValueInvalid(feld: ShellFeld, value: ShellWert | undefined): boolean {
  if (feld.art === 'zahl') return isZahlValuePresentAndInvalid(value)
  return false
}

export function isShellFeldEmpty(feld: ShellFeld, value: ShellWert | undefined): boolean {
  if (feld.art === 'gruppe' || feld.art === 'zwischentitel' || feld.nur_lesen) return false
  if (feld.art === 'mehrfach') {
    if (!Array.isArray(value) || value.length === 0) return true
    return value.some(isBlankFreitextAnswer)
  }
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return isBlankFreitextAnswer(value)
  }
  if (feld.art === 'zahl') {
    if (value == null || value === '') return true
    if (typeof value === 'string') {
      return !WHOLE_NUMBER.test(value) || isZahlOutOfRange(Number(value))
    }
    if (typeof value === 'number') return isZahlOutOfRange(value)
    return true
  }
  if (typeof value === 'string') return isBlankText(value)
  return value == null
}

const SHELL_KEYS = new Set(['nummer', 'titel', 'untertitel', 'abschnitte', 'unterschriften'])
const ABSCHNITT_KEYS = new Set(['id', 'titel', 'text', 'felder'])
const FELD_KEYS = new Set([
  'id',
  'label',
  'art',
  'optionen',
  'gruppen',
  'pflicht',
  'wenn',
  'nur_lesen',
  'hinweis',
  'felder',
  'inline',
  'druck',
  'vortext',
])
const ZWISCHENTITEL_KEYS = new Set(['id', 'label', 'art'])
const VORTEXT_KEYS = new Set(['id'])
const VORTEXT_ARTEN = new Set(['auswahl', 'mehrfach'])
const WENN_KEYS = new Set(['feld', 'ist', 'oder_quelle', 'groesser_als'])
const OPTION_OBJECT_KEYS = new Set(['label', 'freitext'])
const OPTION_GRUPPE_KEYS = new Set(['titel', 'optionen'])

function assertKnownKeys(value: unknown, allowed: ReadonlySet<string>, shellTitel: string, path: string): void {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) {
      throw new Error(`FormRenderer: unbekannter Schlüssel "${key}" bei ${path} (Formular "${shellTitel}")`)
    }
  }
}

function assertValidFeld(feld: ShellFeld, shellTitel: string): void {
  const path = feld.id ? `Feld "${feld.id}"` : 'einem Feld ohne id'
  if (feld.art === 'zwischentitel') {
    assertKnownKeys(feld, ZWISCHENTITEL_KEYS, shellTitel, path)
    if (typeof feld.label !== 'string' || feld.label === '') {
      throw new Error(`FormRenderer: "zwischentitel" ${path} braucht ein label (Formular "${shellTitel}")`)
    }
    return
  }
  assertKnownKeys(feld, FELD_KEYS, shellTitel, path)
  if (feld.vortext !== undefined) assertValidVortext(feld, shellTitel, path)
  if (feld.wenn != null) assertKnownKeys(feld.wenn, WENN_KEYS, shellTitel, `${path}.wenn`)
  for (const option of feld.optionen ?? []) {
    if (typeof option !== 'string') assertKnownKeys(option, OPTION_OBJECT_KEYS, shellTitel, `${path}.optionen`)
  }
  for (const gruppe of feld.gruppen ?? []) {
    assertKnownKeys(gruppe, OPTION_GRUPPE_KEYS, shellTitel, `${path}.gruppen`)
    for (const option of gruppe.optionen ?? []) {
      if (typeof option !== 'string') {
        assertKnownKeys(option, OPTION_OBJECT_KEYS, shellTitel, `${path}.gruppen.optionen`)
      }
    }
  }
  for (const sub of feld.felder ?? []) assertValidFeld(sub, shellTitel)
}

function assertValidVortext(feld: ShellFeld, shellTitel: string, path: string): void {
  const vortext: unknown = feld.vortext
  const fail = (grund: string) => {
    throw new Error(`FormRenderer: ${path}.vortext ${grund} (Formular "${shellTitel}")`)
  }
  if (!VORTEXT_ARTEN.has(feld.art)) fail(`gibt es nur bei "auswahl" und "mehrfach", nicht bei "${feld.art}"`)
  if (vortext == null || typeof vortext !== 'object' || Array.isArray(vortext)) fail('muss ein Objekt {"id": …} sein')
  assertKnownKeys(vortext, VORTEXT_KEYS, shellTitel, `${path}.vortext`)
  const id = (vortext as { id?: unknown }).id
  if (typeof id !== 'string' || id === '') fail('braucht eine id')
}

type ShellIds = { ids: string[]; vortextIds: string[]; wennFelder: string[] }

function collectShellIds(felder: readonly ShellFeld[], out: ShellIds): ShellIds {
  for (const feld of felder) {
    out.ids.push(feld.id)
    if (feld.vortext) {
      out.ids.push(feld.vortext.id)
      out.vortextIds.push(feld.vortext.id)
    }
    if (feld.wenn) out.wennFelder.push(feld.wenn.feld)
    if (feld.felder) collectShellIds(feld.felder, out)
  }
  return out
}

function assertVortextIds(shell: Shell, shellTitel: string): void {
  const { ids, vortextIds, wennFelder } = collectShellIds(shellFelder(shell), { ids: [], vortextIds: [], wennFelder: [] })
  for (const id of vortextIds) {
    if (ids.filter((other) => other === id).length > 1) {
      throw new Error(`FormRenderer: vortext-id "${id}" ist nicht eindeutig (Formular "${shellTitel}")`)
    }
    if (wennFelder.includes(id)) {
      throw new Error(`FormRenderer: ein wenn nennt die vortext-id "${id}" (Formular "${shellTitel}")`)
    }
  }
}

export function assertValidShell(shell: Shell): void {
  const shellTitel = typeof shell.titel === 'string' && shell.titel ? shell.titel : '<ohne titel>'
  assertKnownKeys(shell, SHELL_KEYS, shellTitel, 'der Wurzel')
  shell.abschnitte.forEach((abschnitt, i) => {
    const path = abschnitt.id ? `Abschnitt "${abschnitt.id}"` : `abschnitte[${i}]`
    assertKnownKeys(abschnitt, ABSCHNITT_KEYS, shellTitel, path)
    for (const feld of abschnitt.felder ?? []) assertValidFeld(feld, shellTitel)
  })
  assertVortextIds(shell, shellTitel)
}
